import React from 'react';
import { CaretUp, CaretDown, CaretUpDown, CheckSquare, Square } from '@phosphor-icons/react';

/**
 * `<PowerTable>` — a compact, sortable, row-clickable listing.
 *
 * Props:
 *   columns:  Array<{ key, label, sortable?, className?, cell?(row) => ReactNode, accessor?(row) => primitive }>
 *   rows:     Array<Object>  (rows must have an `id` field)
 *   onRowClick(row):          optional — click anywhere on the row (row cursor becomes pointer)
 *   initialSort:              { key, dir: 'asc'|'desc' } — default sort applied on mount
 *   selection:                optional { isSelected(id), toggle(id) } — enables the leading checkbox column
 *   testIdPrefix:             defaults to 'power-table'
 *   emptyLabel:               shown when rows.length === 0
 *
 * By design this is a *dumb* component: it doesn't fetch, filter, or
 * paginate. The caller pre-filters rows before passing them in — that keeps
 * the parent screens as the single source of truth for their data.
 */
export const PowerTable = ({
  columns,
  rows,
  onRowClick,
  initialSort,
  selection,
  testIdPrefix = 'power-table',
  emptyLabel = 'No records to show.',
}) => {
  const [sort, setSort] = React.useState(initialSort || null);

  const sortedRows = React.useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const getVal = (r) => {
      if (col.accessor) return col.accessor(r);
      const raw = r[sort.key];
      return raw == null ? '' : raw;
    };
    return [...rows].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      // Numeric compare when both are numbers, else string.
      const na = typeof va === 'number' ? va : parseFloat(va);
      const nb = typeof vb === 'number' ? vb : parseFloat(vb);
      const bothNums = !Number.isNaN(na) && !Number.isNaN(nb) && typeof va !== 'string';
      const cmp = bothNums
        ? na - nb
        : String(va).localeCompare(String(vb), 'en', { sensitivity: 'base' });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null; // third click clears sort
    });
  };

  if (!rows || rows.length === 0) {
    return (
      <div
        className="medical-card p-8 text-center text-sm text-[#6D8E87]"
        data-testid={`${testIdPrefix}-empty`}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div
      className="medical-card overflow-hidden"
      data-testid={testIdPrefix}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F9F7] border-b border-[#D5E8E4] sticky top-0 z-10">
            <tr>
              {selection && (
                <th className="px-3 py-3 w-8 text-left"></th>
              )}
              {columns.map((col) => {
                const isActive = sort?.key === col.key;
                const Icon = !col.sortable
                  ? null
                  : !isActive
                    ? CaretUpDown
                    : sort.dir === 'asc'
                      ? CaretUp
                      : CaretDown;
                return (
                  <th
                    key={col.key}
                    className={`px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#3B5A54] ${col.className || ''} ${col.sortable ? 'cursor-pointer select-none hover:text-[#00a69c]' : ''}`}
                    onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    data-testid={`${testIdPrefix}-header-${col.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {Icon && <Icon size={12} weight={isActive ? 'bold' : 'regular'} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={row.id}
                className={`border-b border-[#E2ECEA] last:border-b-0 transition hover:bg-[#F4F9F7] ${
                  onRowClick ? 'cursor-pointer' : ''
                } ${selection?.isSelected(row.id) ? 'bg-[#E2F3F0]' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                data-testid={`${testIdPrefix}-row-${row.id}`}
              >
                {selection && (
                  <td
                    className="px-3 py-3 w-8"
                    onClick={(e) => { e.stopPropagation(); selection.toggle(row.id); }}
                    data-testid={`${testIdPrefix}-checkbox-${row.id}`}
                  >
                    {selection.isSelected(row.id)
                      ? <CheckSquare size={18} weight="fill" className="text-[#00a69c]" />
                      : <Square size={18} className="text-[#6D8E87]" />}
                  </td>
                )}
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-3 text-[#052A23] ${col.className || ''}`}
                  >
                    {col.cell ? col.cell(row) : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
